// src/features/pricing/import/PriceImport.jsx

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Col, Form, Row } from "react-bootstrap";
import { AnimatePresence, motion } from "framer-motion";
import FalconComponentCard from "components/common/FalconComponentCard";
import HorizontalStepper from "features/practitioner/components/HorizontalStepper";
import PriceUploadStep from "./PriceUploadStep";
import PriceCheckingStep from "./PriceCheckingStep";
import PriceResultStep from "./PriceResultStep";
import { CheckCircle, CloudUpload, ShieldCheck } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  resetImportState,
  setActiveStep,
  setInvalidData,
  setTableData,
} from "../config/PriceSlice";
import { CheckNphiesCode } from "../config/PriceApi";
import WizardModal from "components/wizard/WizardModal";
import UploadPracitionerModal from "features/practitioner/components/UploadPracitionerModal";
import UploadPriceModal from "./UploadPriceModal";
import BackButton from "components/common/custom/back-button";
import paths from "routes/paths";

const PriceImport = () => {
  const dispatch = useDispatch();
  const { excelData, validationErrors, activeStep, tableData, inValidData } =
    useSelector((state) => state.price);
  const { allPermissionType } = useSelector((state) => state.role);
  const priceSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Price"
  );

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [payload, setPayload] = useState(false);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      payer: null,
      excelFile: null,
    },
  });

  useEffect(() => {
    dispatch(resetImportState())
  }, [])

  const steps = [
    {
      label: "Upload",
      icon: CloudUpload,
      component: (
        <PriceUploadStep
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      ),
    },
    {
      label: "Checking",
      icon: ShieldCheck,
      component: <PriceCheckingStep />,
    },
    {
      label: "Result",
      icon: CheckCircle,
      component: <PriceResultStep />,
    },
  ];

  useEffect(() => {
    const data = excelData?.map((row, index) => {
      const rowNum = index + 2;
      const rowErrors = validationErrors?.filter((err) => err?.row === rowNum);

      return {
        ...row,
        rowNum,
        isValid: rowErrors?.length === 0,
        errors: rowErrors?.map((e) => e.message),
      };
    });
    dispatch(setTableData(data));
  }, [excelData, validationErrors, dispatch]);

  const handleContinue = async () => {
    if (activeStep === 0) {
      const isValid = await trigger(["payer", "excelFile"]);
      if (isValid) {
        dispatch(setActiveStep(activeStep + 1));
      }
      return;
    }

    if (activeStep === steps.length - 1) {
      const testBody = {
        nphies_data: tableData.reduce((acc, row) => {
          const rowKey = String(row.rowNum);
          acc[rowKey] = {
            nphies_code: row["Nphies Code"],
          };
          return acc;
        }, {}),
      };
      const uploadPayload = {
        price_data: tableData.reduce((acc, row) => {
          const rowKey = String(row.rowNum);
          acc[rowKey] = {
            branch_id: selectedBranch?.branch_id || selectedBranch?.value || "",
            payer_id: watch("payer")?.payer_id || "",
            nphies_code: row["Nphies Code"] || "",
            provider_cde: row["Provider Service Code"] || "",
            provider_cdedesc: row["Provider Service Description"] || "",
            prd_price: row["Price"] || "",
            prd_disc: row["Discount"] || "",
            prd_tax: row["VAT Indicator "] ?? "",
            actv_dte: "",
          };
          return acc;
        }, {}),
      };

      try {
        const response = await dispatch(CheckNphiesCode(testBody)).unwrap();

        if (response?.success) {
          const apiData = response?.data?.nphies_data || {};

          // Extract invalid codes
          const invalidCodes = Object.entries(apiData)
            .filter(([_, val]) => val.status?.toLowerCase() !== "valid")
            .map(([_, val]) => val.nphies_code);

          // Update status for all rows
          const updatedTableData = tableData.map((row) => {
            const isInvalid = invalidCodes.includes(row["Nphies Code"]);

            return {
              ...row,
              isValid: isInvalid ? "Invalid" : "Valid",
            };
          });

          // Store full updated table
          dispatch(setTableData(updatedTableData));

          // Store just invalid ones
          const invalidOnly = updatedTableData.filter(
            (row) => row.isValid === "Invalid"
          );
          if (invalidOnly?.length > 0) {
            setShowErrorModal(true);
            dispatch(setInvalidData(invalidOnly));
          } else {
            setInvalidData([]);
            setPayload(uploadPayload);
            setShowUploadModal(true);
          }
        }
      } catch (error) {
        console.error("❌ Nphies validation failed:", error);
      }

      return;
    }

    dispatch(setActiveStep(activeStep + 1));
  };

  const handleBack = () => {
    if (activeStep > 0) {
      dispatch(setActiveStep(activeStep - 1));
    }
  };

  return (
    <>
      {Number(priceSetting?.add) === 1 ? (
        <Form noValidate className="p-0">
          <FalconComponentCard className="shadow-md">
            <FalconComponentCard.Header className="fixed-header">
              <Row className="w-100 justify-content-between align-items-center g-2">
                <BackButton path={paths.priceList} title="Price Import" />

                <Col xs="auto" className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="success"
                    onClick={handleContinue}
                    disabled={
                      (activeStep === 0 &&
                        (!watch("payer") || !watch("excelFile"))) ||
                      (activeStep === 1 && tableData.some((row) => !row.isValid))
                    }
                  >
                    {activeStep === steps.length - 1 ? "Finish" : "Continue"}
                  </Button>
                </Col>
              </Row>
            </FalconComponentCard.Header>

            <FalconComponentCard.Body
              style={{
                marginTop: "40px",
                overflowY: "auto",
                maxHeight: "calc(100vh - 200px)",
              }}
            >
              <Row>
                <Col xs={12} className="mb-3">
                  <HorizontalStepper
                    isHorizaontal
                    steps={steps}
                    activeStep={activeStep}
                  />
                </Col>
                <Col xs={12}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: -100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                    >
                      {steps[activeStep]?.component || null}
                    </motion.div>
                  </AnimatePresence>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Form>
      ) : (
        <div className="py-6 text-center border rounded-3mb-3">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to import Price.
          </p>
        </div>
      )}
      <WizardModal
        modal={showErrorModal}
        setModal={() => setShowErrorModal(false)}
        title="Invalid Nphies Code Detected"
        description="One or more price have invalid, Please edit  the Nphies Code or delete  before continuing."
      />
      <UploadPriceModal
        payload={payload}
        onClose={() => setShowUploadModal(false)}
        show={showUploadModal}
      />
    </>
  );
};

export default PriceImport;
